import { digger } from '@cbre/digger';
import { areaUomAbbrev, defaultTxt, formatNumber, simpleValueValidation } from '@miq/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { LocaleId } from '../../../../models/locale.model';
import { DataObject, ReportRecord } from '../../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues } from '../../../../models/template.model';
import { currencyLov } from '../us/us.common';

export const PROPERTY_MEASUREMENT_TYPES = {
  warehouse: 'Warehouse Area',
  office: 'Office Area',
  mezzanine: 'Mezzanine Area',
};
export const BLANK_SPACE = ' ';
export const FLOOR_LOADING_UOM_TONS = 'tons/sqm';
export const disclaimerText = {
  [LocaleId.ItalyItalian]:
    'Questi dati sono pubblicati da CBRE a condizione che qualunque negoziazione relativa alla proprietà sia condotta attraverso CBRE. Mentre è stata prestata ogni attenzione nella preparazione degli stessi, CBRE in nome proprio e dei suoi agenti fa presente che: -(i) i dati sono esposti come indicazioni di massima per potenziali acquirenti/locatori e non costituiscono parte di un’offerta o contratto; (ii) qualsiasi rappresentazione incluse le descrizioni, le dimensioni, i riferimenti allo stato dell’immobile, a permessi o licenze per usi o occupazioni, accessi e qualunque altro dettaglio viene fornita in buona fede ed è ritenuta corretta, ma qualsiasi potenziale acquirente o locatore dovrebbe non fare affidamento su tali rappresentazioni, ma verificare le stesse (a proprie cure e spese) in merito alla loro correttezza; (iii) né CBRE, né alcuno dei suoi dipendenti ha alcuna autorità per fornire dichiarazioni o garanzie in relazione all’immobile.',
  [LocaleId.ItalyEnglish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Italy, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.PolandPolish]:
    'NIniejsze informacje są prezentowane przez CBRE przy założeniu, że wszelkie negocjacje dotyczące nieruchomości są prowadzone za ich pośrednictwem. Chociaż przy ich przygotowaniu dołożono wszelkich starań, CBRE, informuje, że:- (i) Szczegóły są przedstawione jako ogólny zarys wskazówek dla potencjalnych nabywców/najemców i nie nie stanowią żadnej części oferty lub umowy. (ii) Wszelkie oświadczenia, w tym opisy, wymiary, odniesienia do warunków, pozwoleń lub licencji na użytkowanie lub zawód, dostęp i wszelkie inne szczegóły są podane w dobrej wierze i są uważane za prawidłowe, ale jakikolwiek przyszły nabywca lub najemca nie powinien na nich polegać jako stwierdzenia lub przedstawienia faktów, ale muszą upewnić się, co do ich poprawności. (iii) Ani CBRE, ani żaden z ich pracowników nie ma żadnych uprawnień do składania jakichkolwiek oświadczeń ani gwarancji w odniesieniu do nieruchomości.',
  [LocaleId.PolandEnglish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Poland, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.SwedenSwedish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Sweden, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.SwedenEnglish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Sweden, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.FinlandFinnish]:
    'CBRE Finland on antanut nämä tiedot sillä edellytyksellä, että kaikki kiinteistöjä koskevat neuvottelut käydään heidän kauttaan. Vaikka niiden valmistelut ovat tehty huolellisesti, CBRE Finland huomauttaa itselleen ja myyjälle/vuokranantajalle, jonka edustajat he ovat, että:- (i) Tiedot ovat esitetty yleisohjeina mahdollisten ostajien/vuokralaisten ohjaamiseksi, eivätkä ole osa tarjousta tai sopimusta. (ii) Kaikki esitykset, mukaan lukien kuvaukset, mitat, viittaukset kuntoon, käyttö- tai ammattikäyttöön liittyvät luvat tai lisenssit, käyttöoikeudet ja kaikki muut tiedot on annettu hyvässä uskossa ja niiden uskotaan olevan oikeita, mutta aikovien ostajien tai vuokralaisten ei tule luottaa niihin väittäminä tai tosiseikkoja, mutta heidän on varmistettava (omalla kustannuksellaan) niiden oikeellisuus. (iii) CBRE Finlandilla tai kenelläkään sen työntekijöistä ei ole valtuuksia esittää kiinteistöjä koskevia esityksiä tai takuita tai antaa niitä.',
  [LocaleId.FinlandEnglish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Finland, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.NorwayNorwegian]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Norge, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.NorwayEnglish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Norway, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
  [LocaleId.NlDutch]:
    'Deze gegevens worden verstrekt door CBRE Nederland met dien verstande dat eventuele onderhandelingen met betrekking tot het onroerend goed door hen worden gevoerd. Hoewel alle zorgvuldigheid is betracht bij het opstellen ervan, deelt CBRE Nederland voor zichzelf en voor de verkoper/verhuurder wiens vertegenwoordiger zij zijn, mede dat: (i) De gegevens zijn uiteengezet als een algemeen overzicht voor het begeleiden van potentiële kopers/huurders en deze geen deel uitmaken van een aanbieding of contract. (ii) Elke weergave, inclusief beschrijvingen, afmetingen, verwijzingen naar staat, toestemmingen of licenties voor gebruik of beroep, toegang en alle andere details te goeder trouw worden gegeven en worden geacht correct te zijn, maar elke beoogde koper of huurder mag er niet op vertrouwen als weergaven van feiten, maar moeten zichzelf (op eigen kosten) zekerheid verschaffen. (iii) Noch CBRE Nederland, noch een van hun werknemers heeft enige bevoegdheid om enige verklaring of garantie te geven met betrekking tot het onroerend goed.',
  [LocaleId.NlEnglish]:
    'These particulars are issued by CBRE on the understanding that any negotiations relating to the property are conducted through them. While every care has been taken in preparing them, CBRE for themselves and for the vendor/lessor whose agents they are, give notice that:- (i) The particulars are set out as a general outline for guiding potential purchasers/tenants and do not constitute any part of an offer or contract. (ii) Any representation including descriptions, dimensions, references to condition, permissions or licenses for uses or occupation, access and any other details are given in good faith and are believed to be correct, but any intending purchaser or tenant should not rely on them as statements or representations of fact but must satisfy themselves (at their own expense) as to their correctness. (iii) Neither CBRE Netherlands, nor any of their employees have any authority to make any or give any representation or warranty in relation to the property.',
};
const TRANSPORT_TYPES = {
  airport: 'airport',
  bus: 'bus',
  ferry: 'ferry',
  freeport: 'free port',
  highway: 'highway',
  metro: 'metro',
  seaport: 'sea port',
  train: 'train',
};
const colloquialLocales = new Set([
  LocaleId.PolandPolish,
  LocaleId.ItalyItalian,
  LocaleId.SwedenSwedish,
  LocaleId.FinlandFinnish,
  LocaleId.NorwayNorwegian,
  LocaleId.NlDutch,
  LocaleId.SpainSpanish,
  LocaleId.AustriaGerman,
  LocaleId.GermanyGerman
]);

const measurementRentLocales = new Set([
  LocaleId.ItalyEnglish,
  LocaleId.ItalyItalian,
  LocaleId.NorwayEnglish,
  LocaleId.NorwayNorwegian,
  LocaleId.NlEnglish,
  LocaleId.NlDutch,
]);

/**
 * Map used to retrieve the location value to be used to format a number
 * using the angular formatNumber function
 *
 * @see angular.formatNumber
 */
const NUMBER_LOCATION_PER_LOCALE = {
  [LocaleId.ItalyItalian]: 'it-IT',
  [LocaleId.NlDutch]: 'nl-NL',
  [LocaleId.AustriaGerman]: 'de-AT',
  [LocaleId.GermanyGerman]: 'de-DE',
};

/**
 * Wrapper to retrieve media info from record
 * @param record
 * @returns
 */
export function displayImageWrapper(record: ReportRecord) {
  return record.associatedProperty
    ? record.associatedProperty.getMediaInformation()
    : record.data.getMediaInformation();
}

/**
 * Templated wording based on locale
 * @param localeId
 * @returns
 */
export function getAvailabilityValueByLocale(localeId: LocaleId): string {
  let availability: string;
  switch (localeId) {
    case LocaleId.PolandPolish:
      availability = 'Natychmiast';
      break;
    case LocaleId.ItalyItalian:
      availability = 'Immediata';
      break;
    case LocaleId.SwedenSwedish:
      availability = 'Omedelbart';
      break;
    case LocaleId.FinlandFinnish:
      availability = 'Heti';
      break;
    case LocaleId.NorwayNorwegian:
      availability = 'Umiddelbart';
      break;
    default:
      availability = 'Immediately';
  }
  return availability;
}

/**
 * Pull measurement space from record based on type
 */
export function getMeasurement(
  record: ReportRecord,
  options,
  measurementSpace: DisplayFnArgumentsInfo,
  rentField: DisplayFnArgumentsInfo,
  measurementType: string,
  localeID: number
): Observable<string> {
  if (measurementSpace == null || measurementSpace.uomArgument == null) {
    return of(BLANK_SPACE);
  }
  const measurementsPath = `property_measurement[property_element_type_desc='${measurementType}']`;
  const spaceVal$ = getSpaceVal(measurementSpace, record, measurementsPath, localeID);
  /* This if should check for rentField != null.
     Since backend is returning the rentField for all catalogs available, the following if checks for match
     in measurementRentLocales.
  */
  if (measurementRentLocales.has(localeID)) {
    const rentVal$ = getRentVal(options, record, measurementsPath, localeID);

    return combineLatest([spaceVal$, rentVal$]).pipe(
      map(([space, rent]) => {
        return `${space}${rent}`;
      })
    );
  }
  return spaceVal$.pipe(map(actualSpaceValue => actualSpaceValue));
}

function getSpaceVal(
  measurementSpace: DisplayFnArgumentsInfo,
  record: ReportRecord,
  measurementsPath: string,
  locale: number
): Observable<string> {
  if (measurementSpace?.uomArgument?.lovValues == null) {
    return of(defaultTxt);
  }
  return measurementSpace.uomArgument.lovValues.pipe(
    startWith([]),
    map((lovs: LovValues[]) => {
      const measurementSize = `property_measurement_size`;
      const spaceFullPath = `${measurementsPath}.${measurementSize}`;
      const measurementUomSize = `property_measurement_size_uom`;
      const spaceUomFullPath = `${measurementsPath}.${measurementUomSize}`;
      const space = getFieldValue(record.data, spaceFullPath, measurementSpace.uomArgument.dataFormat);
      const spaceUom = getFieldValue(record.data, spaceUomFullPath, measurementSpace.uomArgument.dataFormat);
      const lovValue = lovs.find(a => a.key === spaceUom);
      const uomValue = areaUomAbbrev(lovValue != null && lovValue.value != null ? lovValue.value : spaceUom);

      const formattedSpace = space != null ? formatNumberByMIQLocale(space, locale) : defaultTxt;
      return `${formattedSpace} ${uomValue != null ? uomValue : ''}`;
    })
  );
}

function getRentVal(options: any, record: ReportRecord, measurementsPath: string, locale: number): Observable<string> {
  return currencyLov(options).pipe(
    startWith([]),
    map((symbolLob: LovValues[]) => {
      const fullRentFieldPath = `${measurementsPath}.asking_rent_yearly`;
      const fullRentFieldUOMPath = `${measurementsPath}.asking_rent_yearly_uom`;
      const rentFieldValue = digger(record.data, fullRentFieldPath);
      const rentFieldUOMValue = digger(record.data, fullRentFieldUOMPath);
      const symbol = symbolLob.find(a => a.key === rentFieldUOMValue)?.value;
      if (rentFieldValue != null) {
        const formattedRentVal = formatNumberByMIQLocale(rentFieldValue, locale);
        const rentValue = symbol != null ? `${symbol}${formattedRentVal}` : `${formattedRentVal} ${rentFieldUOMValue}`;
        /*
         * Whenever there is a Measurement Rent value the space before the
         * rentValue is needed to correctly concatenate both Measurement Area
         * and Measurement Rent.
         */
        return ` (${rentValue}/yr/sqm)`;
      }
      return '';
    })
  );
}

/**
 * Get transport type from fixed list
 * @param transportType
 * @returns
 */
export function getTransportType(transportType: string): string {
  if (!simpleValueValidation(transportType)) {
    return BLANK_SPACE;
  }
  for (const type in TRANSPORT_TYPES) {
    if (transportType.toLowerCase().includes(TRANSPORT_TYPES[type])) {
      return type;
    }
  }
  return BLANK_SPACE;
}

/**
 * Pull property notes description from record based on description type, using alternate description for non-english locales
 * @param data
 * @param locale
 * @param descriptionType
 * @returns
 */
export function getPropertyNotes(data: DataObject, locale: LocaleId, descriptionType: string): string {
  const propertyNotePath = `property_note[property_notes_type_desc='${descriptionType}']`;
  const locationDescPath = `notes`;
  const locationDescValue = digger(data, `${propertyNotePath}.${locationDescPath}`);
  if (colloquialLocales.has(locale)) {
    const colloquialPath = `${propertyNotePath}.colloquial[property_notes_type_desc='${descriptionType}']`;
    const colloquialValue = digger(data, `${colloquialPath}.${locationDescPath}`);
    if (simpleValueValidation(colloquialValue)) {
      return colloquialValue;
    }
  }
  return simpleValueValidation(locationDescValue) ? locationDescValue : BLANK_SPACE;
}

export function formatNumberByMIQLocale(
  value: string | number,
  miqLocale: number,
  digits?: string,
  defaultText?: string
): string {
  if (value == null) {
    return defaultText;
  }
  const toFormat = typeof value === 'number' ? value : Number(value.replace(/,/g, ''));
  if (isNaN(toFormat)) {
    return defaultText;
  }
  return !!NUMBER_LOCATION_PER_LOCALE[miqLocale]
    ? formatNumber(toFormat, NUMBER_LOCATION_PER_LOCALE[miqLocale], digits, defaultText)
    : formatNumber(toFormat, 'en-US');
}

/**
 * creates a new DisplayFnArgumentsInfo using the provided one as the base and
 * extends it with the location attribute filling it from with the value mapped
 * in the {@link NUMBER_LOCATION_PER_LOCALE}
 *
 * @param arg Argument to extend
 * @param miqLocale Locale to search for
 */
export function addLocationToArgument(arg: DisplayFnArgumentsInfo, miqLocale: LocaleId): DisplayFnArgumentsInfo {
  return { ...arg, location: NUMBER_LOCATION_PER_LOCALE[miqLocale || ''] };
}

/**
 * Helper to retrieve a value from record with optional format
 * @param data
 * @param path
 * @param format
 * @returns
 */
function getFieldValue(data: DataObject, path: string, format?: string): string {
  const value = digger(data, path);
  return simpleValueValidation(value) ? formatNumber(value, undefined, format).toString() : '';
}
